import localforage from 'localforage';
import { logLoginFlow } from '../loginLogger';

const DOING_LOGOUT_KEY = 'doingLogout';
const LOGOUT_TIMEOUT_MS = 2 * 60 * 1000; // 2 minuti

interface DoingLogoutData {
  timestamp: number;
}

/**
 * Imposta il flag di logout in corso con timestamp.
 * Usato quando si avvia un logout per tracciare che è in corso.
 */
export const setDoingLogout = async (): Promise<void> => {
  const data: DoingLogoutData = { timestamp: Date.now() };
  await localforage.setItem(DOING_LOGOUT_KEY, data);
};

/**
 * Rimuove il flag di logout in corso.
 * Usato quando il login è completato con successo o il logout è terminato.
 */
export const clearDoingLogout = async (): Promise<void> => {
  await localforage.removeItem(DOING_LOGOUT_KEY);
};

/**
 * Verifica se c'è un logout valido in corso.
 * Gestisce:
 * - Flag assente → false
 * - Valore 'false' o false → rimuove e ritorna false
 * - Formato legacy ('true' o stringa) → rimuove, logga e ritorna false
 * - Timestamp scaduto (> 2 minuti) → rimuove, logga e ritorna false
 * - Timestamp valido → true (logout effettivamente in corso)
 */
export const isDoingLogoutValid = async (): Promise<boolean> => {
  const value = await localforage.getItem(DOING_LOGOUT_KEY);

  // Flag assente
  if (value === null || value === undefined) {
    return false;
  }

  // Valore esplicitamente false o stringa 'false'
  if (value === false || value === 'false') {
    await localforage.removeItem(DOING_LOGOUT_KEY);
    return false;
  }

  // Formato legacy: stringa 'true' o altro valore non-oggetto
  if (typeof value !== 'object') {
    logLoginFlow('LOGOUT_FLAG_LEGACY_CLEARED', {
      legacyValue: String(value),
    });
    await localforage.removeItem(DOING_LOGOUT_KEY);
    return false;
  }

  // Verifica che sia un oggetto con timestamp valido
  const data = value as DoingLogoutData;
  if (typeof data.timestamp !== 'number') {
    logLoginFlow('LOGOUT_FLAG_LEGACY_CLEARED', {
      reason: 'invalid_timestamp_format',
    });
    await localforage.removeItem(DOING_LOGOUT_KEY);
    return false;
  }

  // Verifica se il timestamp è scaduto (> 2 minuti)
  const ageMs = Date.now() - data.timestamp;
  if (ageMs > LOGOUT_TIMEOUT_MS) {
    logLoginFlow('LOGOUT_FLAG_EXPIRED', {
      ageMs,
      timeoutMs: LOGOUT_TIMEOUT_MS,
      ageSecs: Math.round(ageMs / 1000),
    });
    await localforage.removeItem(DOING_LOGOUT_KEY);
    return false;
  }

  // Logout valido in corso
  return true;
};

/**
 * Migra eventuali flag legacy da localStorage a localforage.
 * Chiamare all'avvio per pulire vecchi dati.
 * Non migra il valore, lo rimuove semplicemente (considerato stale).
 */
export const migrateLegacyDoingLogout = (): void => {
  const legacyValue = localStorage.getItem(DOING_LOGOUT_KEY);
  if (legacyValue !== null) {
    logLoginFlow('LOGOUT_FLAG_LEGACY_MIGRATED', {
      fromLocalStorage: true,
      legacyValue,
    });
    localStorage.removeItem(DOING_LOGOUT_KEY);
  }
};
